// The puzzle menu of the top bar (spec/dashboard.md section 10.7): the merged @menuItem tree, Extensions Log (n),
// Reload Extensions, Scripts IDE, and the host's state.
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useGame } from '../shell/GameContext';
import { routes } from '../shell/routes';
import { Button } from '../ui/Button';
import { Puzzle } from '../ui/icons';
import { Popover } from '../ui/Popover';
import { useToasts } from '../ui/Toast';
import type { ExtensionHost, MenuNode } from './host';

export function ExtensionsMenu({ host, onOpenLogs }: { host: ExtensionHost | null; onOpenLogs?: () => void }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { org, game } = useGame();
  const { branch = '' } = useParams();
  const toasts = useToasts();
  if (!host || !branch) return null;
  const state = host.state;
  const count = state.kind === 'ready' ? state.extensions.length : 0;
  if (state.kind === 'idle' || (state.kind === 'ready' && count === 0 && state.errors.length === 0)) return null;
  const tree = host.menu();
  const logCount = host.logs.length;
  const run = async (id: string) => {
    setAnchor(null);
    await host.runMenu(id);
  };
  const reload = async () => {
    setAnchor(null);
    const s = await host.load();
    if (s.kind === 'ready') toasts.success(`${s.extensions.length} extension${s.extensions.length === 1 ? '' : 's'} loaded${s.errors.length ? `; ${s.errors.length} failed — see the log` : ''}.`);
    else if (s.kind === 'failed') toasts.error(`Extensions failed to load: ${s.message}`);
  };
  const stateText = state.kind === 'loading' ? 'loading…' : state.kind === 'failed' ? `failed: ${state.message}` : `${count} loaded${state.kind === 'ready' && state.errors.length ? `, ${state.errors.length} failed` : ''}`;
  return (
    <>
      <Button variant="ghost" size="s" onClick={(e) => setAnchor(e.currentTarget)} aria-haspopup="dialog" aria-label="Extensions menu" title={`Extensions: ${stateText}`} data-testid="extensions-menu-button">
        <Puzzle aria-hidden="true" />{logCount > 0 ? <span className="badge badge-neutral small">{logCount}</span> : null}
      </Button>
      <Popover anchor={anchor} open={anchor !== null} onClose={() => setAnchor(null)} label="Extensions" className="extensions-menu">
        <div className="muted small">Extensions: {stateText}</div>
        {tree.length > 0 && (
          <div className="extensions-tree" role="menu">
            {tree.map((n) => (
              <MenuBranch key={n.label} node={n} depth={0} onRun={run} />
            ))}
          </div>
        )}
        <hr />
        <button type="button" role="menuitem" className="picker-entry" onClick={() => { setAnchor(null); onOpenLogs?.(); }}>
          Extensions Log ({logCount})
        </button>
        <button type="button" role="menuitem" className="picker-entry" onClick={() => void reload()}>
          Reload Extensions
        </button>
        <Link role="menuitem" className="picker-entry" to={routes.branch(org, game.slug, branch, 'extensions')} onClick={() => setAnchor(null)}>
          Scripts IDE
        </Link>
      </Popover>
    </>
  );
}

function MenuBranch({ node, depth, onRun }: { node: MenuNode; depth: number; onRun: (id: string) => void }) {
  if (node.item && node.children.length === 0) {
    return (
      <button type="button" role="menuitem" className="picker-entry" style={{ paddingLeft: 8 + depth * 14 }} onClick={() => onRun(node.item!.id)} title={node.item.extension}>
        {node.label}
      </button>
    );
  }
  return (
    <div role="group" aria-label={node.label}>
      <div className="picker-group-title" style={{ paddingLeft: 8 + depth * 14 }}>
        {node.label}
      </div>
      {node.children.map((c) => (
        <MenuBranch key={`${c.label}:${c.item?.id ?? ''}`} node={c} depth={depth + 1} onRun={onRun} />
      ))}
      {node.item && (
        <button type="button" role="menuitem" className="picker-entry" style={{ paddingLeft: 8 + (depth + 1) * 14 }} onClick={() => onRun(node.item!.id)} title={node.item.extension}>
          {node.label}
        </button>
      )}
    </div>
  );
}
